import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Download, Copy, Loader2, FileCode, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useAnalytics } from '@/hooks/useAnalytics';
import { AdfElementNode } from '@/hooks/useAdfStandard';
import { XmlGenerationOptions } from '@/hooks/useAdfBuilder';

type AdfExportFormat = 'xml' | 'xsd' | 'csv';

interface FormatOption {
  id: AdfExportFormat;
  label: string;
  description: string;
  icon: React.ElementType;
}

const FORMAT_OPTIONS: FormatOption[] = [
  {
    id: 'xml',
    label: 'XML Template',
    description: 'Selected elements as XML template with example values',
    icon: FileCode,
  },
  {
    id: 'xsd',
    label: 'XSD Schema',
    description: 'XML Schema Definition for validation',
    icon: FileText,
  },
  {
    id: 'csv',
    label: 'Element Reference CSV',
    description: 'Spreadsheet with element details and hierarchy',
    icon: FileSpreadsheet,
  },
];

interface AdfExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedElements: AdfElementNode[];
  generateXml: (options: XmlGenerationOptions) => string;
}

function generateXsdSchema(elements: AdfElementNode[]): string {
  const lines: string[] = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<xs:schema xmlns:xs="http://www.w3.org/2001/XMLSchema">',
    '',
    '  <!-- ADF Schema - Generated Subset -->',
    '',
  ];
  
  // Build element map for lookups
  const elementMap = new Map<string, AdfElementNode>();
  const flatten = (nodes: AdfElementNode[]) => {
    nodes.forEach(node => {
      elementMap.set(node.id, node);
      flatten(node.children);
    });
  };
  flatten(elements);
  
  // Generate element definitions
  const processElement = (element: AdfElementNode, indent: number = 1) => {
    const indentStr = '  '.repeat(indent);
    const hasChildren = element.children.length > 0;
    const minOccurs = element.is_required ? '1' : '0';
    const maxOccurs = element.is_repeatable ? 'unbounded' : '1';
    
    if (hasChildren) {
      lines.push(`${indentStr}<xs:element name="${element.tag_name}" minOccurs="${minOccurs}" maxOccurs="${maxOccurs}">`);
      
      // Add documentation
      if (element.description) {
        lines.push(`${indentStr}  <xs:annotation>`);
        lines.push(`${indentStr}    <xs:documentation>${element.description}</xs:documentation>`);
        lines.push(`${indentStr}  </xs:annotation>`);
      }
      
      lines.push(`${indentStr}  <xs:complexType>`);
      lines.push(`${indentStr}    <xs:sequence>`);
      
      element.children.forEach(child => {
        processElement(child, indent + 3);
      });
      
      lines.push(`${indentStr}    </xs:sequence>`);
      
      // Add attributes if any
      if (element.attributes && typeof element.attributes === 'object') {
        const attrs = element.attributes as Record<string, unknown>;
        Object.keys(attrs).forEach(attrName => {
          lines.push(`${indentStr}    <xs:attribute name="${attrName}" type="xs:string" />`);
        });
      }
      
      lines.push(`${indentStr}  </xs:complexType>`);
      lines.push(`${indentStr}</xs:element>`);
    } else {
      // Leaf element
      lines.push(`${indentStr}<xs:element name="${element.tag_name}" type="xs:string" minOccurs="${minOccurs}" maxOccurs="${maxOccurs}">`);
      if (element.description) {
        lines.push(`${indentStr}  <xs:annotation>`);
        lines.push(`${indentStr}    <xs:documentation>${element.description}</xs:documentation>`);
        lines.push(`${indentStr}  </xs:annotation>`);
      }
      lines.push(`${indentStr}</xs:element>`);
    }
  };
  
  // Process root elements
  elements.forEach(el => processElement(el));
  
  lines.push('');
  lines.push('</xs:schema>');
  
  return lines.join('\n');
}

function generateElementCsv(elements: AdfElementNode[]): string {
  const rows: string[][] = [
    ['Path', 'Tag Name', 'Display Name', 'Required', 'Repeatable', 'Description'],
  ];
  
  const processElement = (element: AdfElementNode, path: string = '') => {
    const fullPath = path ? `${path}/${element.tag_name}` : element.tag_name;
    
    rows.push([
      fullPath,
      element.tag_name,
      element.display_name,
      element.is_required ? 'Yes' : 'No',
      element.is_repeatable ? 'Yes' : 'No',
      (element.description || '').replace(/"/g, '""'),
    ]);
    
    element.children.forEach(child => processElement(child, fullPath));
  };
  
  elements.forEach(el => processElement(el));
  
  return rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
}

function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function AdfExportDialog({
  open,
  onOpenChange,
  selectedElements,
  generateXml,
}: AdfExportDialogProps) {
  const [selectedFormat, setSelectedFormat] = useState<AdfExportFormat>('xml');
  const [isExporting, setIsExporting] = useState(false);
  const { trackAdfExport } = useAnalytics();

  const generatedContent = useMemo(() => {
    switch (selectedFormat) {
      case 'xml':
        return generateXml({
          includeComments: true,
          includeExampleValues: true,
          indentSize: 2,
        });
      case 'xsd':
        return generateXsdSchema(selectedElements);
      case 'csv':
        return generateElementCsv(selectedElements);
      default:
        return '';
    }
  }, [selectedFormat, selectedElements, generateXml]);

  const handleDownload = async () => {
    setIsExporting(true);
    try {
      let filename: string;
      let mimeType: string;
      
      switch (selectedFormat) {
        case 'xml':
          filename = 'adf-template.xml';
          mimeType = 'application/xml';
          break;
        case 'xsd':
          filename = 'adf-schema.xsd';
          mimeType = 'application/xml';
          break;
        case 'csv':
          filename = 'adf-elements.csv';
          mimeType = 'text/csv';
          break;
        default:
          throw new Error('Unknown format');
      }
      
      downloadFile(generatedContent, filename, mimeType);
      trackAdfExport(selectedFormat, 'download');
      toast.success('Export downloaded successfully');
      onOpenChange(false);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const handleCopy = async () => {
    setIsExporting(true);
    try {
      await navigator.clipboard.writeText(generatedContent);
      trackAdfExport(selectedFormat, 'copy');
      toast.success('Copied to clipboard');
    } catch (error) {
      toast.error('Copy failed');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Export ADF Subset</DialogTitle>
          <DialogDescription>
            Select an export format for your ADF element selection.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[50vh]">
          <RadioGroup
            value={selectedFormat}
            onValueChange={(value) => setSelectedFormat(value as AdfExportFormat)}
            className="space-y-3"
          >
            {FORMAT_OPTIONS.map((option) => (
              <div
                key={option.id}
                className="flex items-start space-x-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setSelectedFormat(option.id)}
              >
                <RadioGroupItem value={option.id} id={option.id} className="mt-1" />
                <div className="flex-1 space-y-1">
                  <Label
                    htmlFor={option.id}
                    className="flex items-center gap-2 cursor-pointer font-medium"
                  >
                    <option.icon className="h-4 w-4 text-muted-foreground" />
                    {option.label}
                  </Label>
                  <p className="text-xs text-muted-foreground">{option.description}</p>
                </div>
              </div>
            ))}
          </RadioGroup>
        </ScrollArea>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleCopy} disabled={isExporting}>
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Copy className="h-4 w-4 mr-2" />
            )}
            Copy
          </Button>
          <Button onClick={handleDownload} disabled={isExporting}>
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
